import React, { useState, useEffect, useMemo } from 'react';
import { Link as RouterLink } from 'react-router';
import _ from 'lodash';
import makeStyles from '@mui/styles/makeStyles';

import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import Breadcrumbs from '@mui/material/Breadcrumbs';
import Link from '@mui/material/Link';
import AddIcon from '@mui/icons-material/Add';
import Button from '@mui/material/Button';
import Container from '@mui/material/Container';
import Icon from '@mdi/react';
import { mdiMapSearch } from '@mdi/js';

import { SearchInput } from 'widgets-v6/standard-input';
import { SimpleSelect } from 'widgets-v6/select';
import { AudienceCard } from '../modules/audience-card';

import { AudienceTable } from '../modules/audience-table';
import { isInternalUser } from 'states/profile/business-rules';
import FlexSegmentsForm from 'forms/flex-segments-form';
import { useOverallTableDashboardData } from '../overall-dashboard/v2/hook';

const sortingOptions = [
    {
        label: 'Sort by: Newest',
        value: 'id',
        direction: 'desc',
    },
    {
        label: 'Sort by: Name',
        value: 'name',
        direction: 'asc',
    },
];

const useStyles = makeStyles(() => ({
    iconWrapper: {
        display: 'inline-flex',
        height: '112px',
        width: '112px',
        backgroundColor: 'rgba(12, 186, 170, 0.12)',
        borderRadius: '50%',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#098679',
    },
}));

const FlexCustomLayout = ({
    audienceSegments,
    openFormModal,
    onSave,
    onSearch,
    activeAudience,
    organizationId,
}) => {
    const classes = useStyles();
    const { canEditAudiences } = useOverallTableDashboardData();
    const [audiences, setAudiences] = useState(audienceSegments);
    const [activeSort, setActiveSort] = useState({ value: 'id', direction: 'desc' });

    useEffect(() => {
        setAudiences(audienceSegments);
    }, [audienceSegments]);

    const handleSortChange = event => {
        const { value, direction } = _.find(sortingOptions, { value: event.target.value });
        setActiveSort({ value, direction });
    };

    const debouncedSearch = useMemo(() => {
        return _.debounce(value => onSearch(value), 200);
    }, []);

    const handleSearch = e => {
        const { value } = e.target;
        debouncedSearch(value);
    };

    const sortedAudiences = _.orderBy(audiences, [activeSort.value], [activeSort.direction]);

    const linkToAudienceTab = {
        pathname: '/audiences/all',
        query: { audienceTab: 'custom' },
    };

    return (
        <Box padding={4}>
            <Breadcrumbs aria-label="breadcrumb">
                <RouterLink
                    color="inherit"
                    component={Link}
                    to={linkToAudienceTab}
                    underline="none"
                >
                    Your Custom Audiences
                </RouterLink>
                <Typography color="textPrimary">Flex Geolocation</Typography>
            </Breadcrumbs>
            <Container>
                <Grid container>
                    <Grid item xs={2} alignItems="center" style={{ textAlign: 'center' }}>
                        <Box className={classes.iconWrapper}>
                            <Icon path={mdiMapSearch} size={3} />
                        </Box>
                    </Grid>
                    <Grid item xs={8}>
                        <Typography variant="h3" gutterBottom>
                            Flex Geolocation
                        </Typography>
                        <Typography variant="h6" color="textSecondary" gutterBottom>
                            Create your own Flex Geolocation segments by searching through
                            Cleanlist's points of interest database, or by uploading your own store
                            list.
                        </Typography>
                        <Grid container>
                            <Grid item>
                                <Typography variant="subtitle2" color="textSecondary">
                                    Total
                                </Typography>
                                <Typography variant="h6">
                                    {audienceSegments ? audienceSegments.length : 0}
                                </Typography>
                            </Grid>
                        </Grid>
                        <Grid item>
                            {canEditAudiences && (
                                <Box mb={2} mt={2}>
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        onClick={() => openFormModal()}
                                    >
                                        <AddIcon /> New Audience
                                    </Button>
                                </Box>
                            )}
                        </Grid>
                    </Grid>
                </Grid>
                <Box mb={2.5} mt={5}>
                    <Grid container justifyContent="space-between">
                        <Grid item xs={7}>
                            <Grid container spacing={2} alignItems="center">
                                <Grid item>
                                    <SearchInput
                                        placeholder="Filter Audiences"
                                        onChange={handleSearch}
                                    />
                                </Grid>
                            </Grid>
                        </Grid>
                        <Grid item xs={3}>
                            <SimpleSelect
                                options={sortingOptions}
                                value={activeSort.value}
                                onChange={handleSortChange}
                            />
                        </Grid>
                    </Grid>
                </Box>
                <AudienceTable
                    audiences={sortedAudiences}
                    cardSize="small"
                    activeAudience={activeAudience}
                    renderContent={segment => (
                        <AudienceCard
                            onClick={
                                !isInternalUser() && segment.poi_source === 'legacy'
                                    ? null
                                    : () => openFormModal(segment.id)
                            }
                            segment={segment}
                            displayId={true}
                            active={segment.id === activeAudience}
                            organizationId={organizationId}
                        />
                    )}
                />
                <FlexSegmentsForm onSave={onSave} type="custom" />
            </Container>
        </Box>
    );
};

export default FlexCustomLayout;
